import { EventEmitter, Injectable, Output, inject } from '@angular/core';
import { Locale, enUS, he } from 'date-fns/locale';
import { DOCUMENT } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';

export enum AppLanguage {
  ENGLISH = 'en',
  HEBREW = 'he',
}

@Injectable({
  providedIn: 'root',
})
export class AppTranslateService {
  @Output() languageChangeEmitter = new EventEmitter<void>();
  private currentLanguage: AppLanguage = AppLanguage.ENGLISH;

  private readonly translate = inject(TranslateService);
  private readonly document = inject(DOCUMENT);

  /**
   * Sets the language to use in the application.
   * Accepts only values from the AppLanguage enum.
   * If no valid language is provided, the browser’s language is used if supported;
   * otherwise, it falls back to English.
   *
   * @param language Optional language from the AppLanguage enum.
   */
  public setLanguage(language: AppLanguage = AppLanguage.ENGLISH): void {
    this.translate.addLangs([AppLanguage.ENGLISH, AppLanguage.HEBREW]);
    this.translate.setDefaultLang(language);

    if (language && this.isSupportedLanguage(language)) {
      this.currentLanguage = language;
    } else {
      const browserLang = this.translate.getBrowserLang();
      this.currentLanguage = browserLang && this.isSupportedLanguage(browserLang) ? (browserLang as AppLanguage) : AppLanguage.ENGLISH;
    }

    this.translate.use(this.currentLanguage);
    this.setHtmlDirAttribute();
    this.languageChangeEmitter.emit();
  }

  /**
   * Returns the current language.
   */
  public getCurrentLanguage(): AppLanguage {
    return this.currentLanguage;
  }

  /**
   * Checks if a given language string is one of the supported languages.
   *
   * @param lang The language code to check.
   */
  private isSupportedLanguage(lang: string): lang is AppLanguage {
    return [AppLanguage.ENGLISH, AppLanguage.HEBREW].includes(lang as AppLanguage);
  }

  /**
   * Sets the `dir` attribute on the HTML element based on the current language.
   */
  private setHtmlDirAttribute(): void {
    const dir = this.currentLanguage === AppLanguage.HEBREW ? 'rtl' : 'ltr';
    if (this.document && this.document.documentElement) {
      this.document.documentElement.dir = dir;
    }
  }

  public isRTL(): boolean {
    return document.documentElement.dir === 'rtl';
  }

  public getLang(): { locale: Locale } {
    const locales = { he, enUS };
    const currentLang = this.currentLanguage === 'en' ? 'enUS' : 'he';
    return { locale: locales[currentLang] };
  }
}
